import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Flex } from '@optiaxiom/react';
import {
  applyEdgeChanges,
  applyNodeChanges,
  Edge,
  Node,
  OnEdgesChange,
  OnNodesChange,
} from '@xyflow/react';

import { workflowAgentAtom } from '@frontend/atoms';
import { Sidebar } from '@frontend/components';
import { ConfirmationModal } from '@frontend/components/confirmationModal';

import { SpecializedAgentCreationForm } from '../../specialized-agent/agent-form/SpecializedAgentForm';
import { useNodeValidation } from '../hooks/useNodeValidation';
import { useWorkflow } from '../hooks/useWorkflow';
import { useWorkflowCanvas } from '../hooks/useWorkflowCanvas';
import { useWorkflowDirtyState } from '../hooks/useWorkflowDirtyState';

import WorkFlowLeftSidebar, { WorkflowRightSidebar } from './workflow-sidebar';
import { styles } from './WorkflowBuilder.css';
import { WorkflowBuilderTopbar } from './WorkflowBuilderTopbar';
import { WorkflowCanvas } from './WorkflowCanvas';

type WorkflowBuilderProps = {
  onClose: () => void;
  onSave: (draftNodes: Node[], draftEdges: Edge[]) => void;
  initialNodes: Node[];
  initialEdges: Edge[];
  isEditing: boolean;
  onAutoSaveForFirstNode?: (updatedNodes: Node[]) => void;
  isAutoSaving?: boolean;
};

export function WorkflowBuilder({
  onClose,
  onSave,
  initialNodes,
  initialEdges,
  isEditing,
  onAutoSaveForFirstNode,
  isAutoSaving,
}: WorkflowBuilderProps) {
  const { getNodeConfig } = useWorkflow();

  // Local draft state
  const [draftNodes, setDraftNodes] = useState<Node[]>(initialNodes);
  const [draftEdges, setDraftEdges] = useState<Edge[]>(initialEdges);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [forcedSidebarNode, setForcedSidebarNode] = useState<Node | null>(null);
  const [hasUnsavedAsyncNode, setHasUnsavedAsyncNode] = useState(false);
  const [isSACreationOpen, setIsSACreationOpen] = useState(false);
  const [agentFormData] = useAtom(workflowAgentAtom);

  useEffect(() => {
    setDraftNodes(initialNodes);
    setDraftEdges(initialEdges);
  }, [initialNodes, initialEdges]);

  // Use dirty state hook for unsaved changes detection
  const { showUnsavedChangesModal, handleClose, handleConfirmClose, handleCancelClose } =
    useWorkflowDirtyState({
      draftNodes,
      draftEdges,
      initialNodes,
      initialEdges,
      onClose,
    });

  const handleMenuClick = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const handleNodeClick = (_event: React.MouseEvent, node: Node) => {
    if (isSACreationOpen) {
      setIsSACreationOpen(false); // Close SA creation sidebar
    }
    setSelectedNode(node);
  };

  const handleOpenSACreation = () => {
    setIsSACreationOpen(true);
    setSelectedNode(null); // Close node config sidebar
    setForcedSidebarNode(null);
  };

  const handleCloseSACreation = () => {
    setIsSACreationOpen(false);
  };

  const handleNodeDropped = useCallback(() => {
    setSelectedNode(null);
    setForcedSidebarNode(null);
  }, [setSelectedNode, setForcedSidebarNode]);

  const handleAsyncNodeDropped = useCallback(
    (node: Node) => {
      setSelectedNode(node);
      setForcedSidebarNode(node);
      setHasUnsavedAsyncNode(true);
    },
    [setSelectedNode, setForcedSidebarNode, setHasUnsavedAsyncNode]
  );

  const handleFirstNodeDrop = useCallback(
    (updatedNodes: Node[]) => {
      if (!isEditing && onAutoSaveForFirstNode) {
        onAutoSaveForFirstNode(updatedNodes);
      }
    },
    [isEditing, onAutoSaveForFirstNode]
  );

  const handleNodeConfigClose = () => {
    if (forcedSidebarNode) {
      const nodeType = getNodeConfig(forcedSidebarNode.type || '');
      if (nodeType?.onAsyncSave) {
        return;
      }
      handleNodeDelete([forcedSidebarNode]);
    }

    setSelectedNode(null);
    setForcedSidebarNode(null);
  };

  // Local handlers for draft state
  const onNodesChange: OnNodesChange = useCallback(changes => {
    setDraftNodes(nodes => applyNodeChanges(changes, nodes));
  }, []);

  const onEdgesChange: OnEdgesChange = useCallback(changes => {
    setDraftEdges(edges => applyEdgeChanges(changes, edges));
  }, []);

  // Handle save with draft data
  const handleSave = useCallback(() => {
    onSave(draftNodes, draftEdges);
  }, [onSave, draftNodes, draftEdges]);

  const {
    onConnect,
    isValidConnection,
    onNodesDelete,
    onEdgesDelete,
    onNodeUpdate,
    handleDragOver,
    handleDrop,
    nodeTypes,
    reactFlowWrapper,
  } = useWorkflowCanvas({
    nodes: draftNodes,
    edges: draftEdges,
    setNodes: setDraftNodes,
    setEdges: setDraftEdges,
    onNodeDropped: handleNodeDropped,
    onAsyncNodeDropped: handleAsyncNodeDropped,
    onFirstNodeDrop: handleFirstNodeDrop,
  });

  const { getNodesWithErrors, validateNodesWithConnectivity } = useNodeValidation();

  const validationState = useMemo(() => {
    const validationResult = validateNodesWithConnectivity(draftNodes, draftEdges);
    const nodesWithErrors = getNodesWithErrors();

    // Count connectivity errors separately
    const connectivityErrors = validationResult.errors.filter(
      error => error.parameterName === 'connectivity'
    );
    const parameterErrors = validationResult.errors.filter(
      error => error.parameterName !== 'connectivity'
    );

    const connectivityErrorCount = connectivityErrors.length;
    const parameterErrorCount = parameterErrors.length;

    let disabledReason: string | undefined;
    if (!validationResult.isValid) {
      const parts: string[] = [];

      if (parameterErrorCount > 0) {
        parts.push(
          `${parameterErrorCount} node${parameterErrorCount === 1 ? ' has' : 's have'} validation errors`
        );
      }

      if (connectivityErrorCount > 0) {
        parts.push(
          `${connectivityErrorCount} node${connectivityErrorCount === 1 ? ' is' : 's are'} not connected`
        );
      }

      disabledReason = `Cannot save: ${parts.join(', ')}. Please fix all issues.`;
    }

    return {
      // For sidebar - parameter errors only
      hasParameterErrors: parameterErrorCount > 0,

      // For save button - connectivity errors only
      hasConnectivityErrors: connectivityErrorCount > 0,

      // For save button - any errors (parameter + connectivity)
      hasAnyErrors: !validationResult.isValid,

      // Granular error counts for better UX
      parameterErrorCount,
      connectivityErrorCount,

      // Existing states for backward compatibility
      hasErrors: !validationResult.isValid,
      errorCount: validationResult.errors.length,
      nodesWithErrors,
      disabledReason,
    };
  }, [draftNodes, draftEdges, validateNodesWithConnectivity, getNodesWithErrors]);

  const handleNodeUpdate = (nodeId: string, data: Record<string, unknown>) => {
    onNodeUpdate(nodeId, data);

    if (forcedSidebarNode && forcedSidebarNode.id === nodeId) {
      const nodeType = getNodeConfig(forcedSidebarNode.type || '');
      if (nodeType?.onAsyncSave && data) {
        setForcedSidebarNode(null);
        setHasUnsavedAsyncNode(false);
      }
    }
  };

  const handleNodeDelete = (nodes: Node[]) => {
    onNodesDelete(nodes);

    // Close sidebar if the deleted node is currently selected
    if (selectedNode && nodes.some(node => node.id === selectedNode.id)) {
      setSelectedNode(null);
    }

    if (forcedSidebarNode && nodes.some(node => node.id === forcedSidebarNode.id)) {
      setForcedSidebarNode(null);
      setHasUnsavedAsyncNode(false);
    }
  };

  const isEmptyStepsWorkflow = useMemo(() => {
    return agentFormData?.nodes?.length === 0;
  }, [agentFormData?.nodes]);

  const nodesWithAsyncFlags = useMemo(() => {
    if (!forcedSidebarNode) return draftNodes;

    return draftNodes.map(node => {
      if (node.id === forcedSidebarNode.id) {
        return {
          ...node,
          data: {
            ...node.data,
            isUnsavedAsync: true,
          },
        };
      }
      return node;
    });
  }, [draftNodes, forcedSidebarNode]);

  return (
    <Flex className={styles.layoutContainer}>
      <WorkflowBuilderTopbar
        onMenuClick={handleMenuClick}
        onSave={handleSave}
        onClose={handleClose}
        isMenuOpen={isSidebarOpen}
        title={agentFormData?.name || 'Workflow Agent Name'}
        isSaveDisabled={validationState.hasAnyErrors || hasUnsavedAsyncNode}
        saveDisabledReason={
          hasUnsavedAsyncNode
            ? 'Please save or delete the unsaved node before saving the workflow'
            : validationState.disabledReason
        }
        isAutoSaving={isAutoSaving}
      />
      <div className={styles.sidebarArea}>
        <Sidebar open={isSidebarOpen && !isSACreationOpen} initialWidth={350} dock="left" top={0}>
          <WorkFlowLeftSidebar
            isEmptyStepsWorkflow={isEmptyStepsWorkflow}
            hasValidationErrors={validationState.hasParameterErrors}
            hasUnsavedAsyncNode={hasUnsavedAsyncNode}
            onOpenSACreation={handleOpenSACreation}
          />
        </Sidebar>
        <Sidebar open={isSACreationOpen} initialWidth={500} dock="left" top={0}>
          {isSACreationOpen && (
            <SpecializedAgentCreationForm isEmbedded onClose={handleCloseSACreation} />
          )}
        </Sidebar>
        <div className={styles.mainContent}>
          <WorkflowCanvas
            nodes={nodesWithAsyncFlags}
            edges={draftEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            onConnect={onConnect}
            isValidConnection={isValidConnection}
            onNodesDelete={handleNodeDelete}
            onEdgesDelete={onEdgesDelete}
            handleDragOver={handleDragOver}
            handleDrop={handleDrop}
            nodeTypes={nodeTypes}
            reactFlowWrapper={reactFlowWrapper}
          />
        </div>
        <Sidebar open={!!selectedNode} dock="right" top={0} initialWidth={420}>
          <WorkflowRightSidebar
            selectedNode={selectedNode}
            onClose={handleNodeConfigClose}
            onNodeUpdate={handleNodeUpdate}
            onNodeDelete={handleNodeDelete}
            isForced={!!forcedSidebarNode && forcedSidebarNode.id === selectedNode?.id}
          />
        </Sidebar>
      </div>

      <ConfirmationModal
        isShown={showUnsavedChangesModal}
        title="Unsaved Changes"
        onCancel={handleCancelClose}
        onConfirm={handleConfirmClose}
        cancelLabel="Stay"
        action="Discard Changes"
        appearance="danger"
      >
        You have unsaved changes in the workflow. If you close now, all changes will be lost.
      </ConfirmationModal>
    </Flex>
  );
}
